import {useCallback, useEffect, useLayoutEffect} from 'react';

import afterFrame from '../../afterFrame';
import {getRumViewStartTimestamp} from '../datadogRumTimings';
import {
  PANEL_BANK_SEARCH_MAX_PAIRING_DURATION_MS,
  panelBankSearchAppliedDuration,
  panelBankSearchClearMark,
  panelBankSearchFirstInputToSearchAppliedDuration,
  panelBankSearchHistoryItemMark,
} from '../perfObserver/marks/panelBankSearchMarks';

export type SearchInputSource = 'clear' | 'history_item' | 'typing';

export type PanelSearchInputEditContext = {
  // True when the browser reports a deletion input, including backspace/delete.
  hadDeletion: boolean;
  // True when the edit starts before the current value's end, so it was not a
  // simple append. Beginning- and middle-of-string edits both count.
  hadNonAppendEdit: boolean;
  // True when the value change came from a paste; one paste counts as one typed
  // input for typingInputCount.
  hadPaste: boolean;
  // True when the edit replaced selected text or the browser reported a
  // replacement input.
  hadReplacement: boolean;
};

export type PanelSearchInputSearchContext =
  | {
      editContext?: PanelSearchInputEditContext;
      inputSource: 'typing';
    }
  | {
      inputSource: 'clear' | 'history_item';
    };

type PanelSearchFirstInputSearchContext = {
  debounceMs: number;
  appliedInputSource: SearchInputSource;
  firstInputSource: SearchInputSource;
  isClearingSearch: boolean;
  supersededTypingInputCount: number;
  typingInputCount: number;
} & PanelSearchInputEditContext;

type PanelSearchAppliedSearchContext = {
  debounceMs: number;
  inputSource: SearchInputSource;
  isClearingSearch: boolean;
  supersededTypingInputCount: number;
  typingInputCount: number;
} & PanelSearchInputEditContext;

type PanelSearchFirstInputContext = {
  search: PanelSearchFirstInputSearchContext;
};

type PanelSearchAppliedContext = {
  search: PanelSearchAppliedSearchContext;
};

type PanelSearchState = {
  // Latest value-change source, so pain can be segmented by typing, clear, or
  // history item selection at the point feedback finally appears.
  appliedInputSource: SearchInputSource;
  // OR-ed edit flags across the whole burst, so a long burst can show whether
  // the user was correcting/replacing/pasting rather than simply appending.
  burstEditContext: PanelSearchInputEditContext;
  // Search debounce active for this operation, so wait time can be separated
  // from configuration-driven delay versus render/filter overhead.
  debounceMs: number;
  // Source that started the burst, kept stable to explain what triggered the
  // full wait from first input to applied feedback.
  firstInputSource: SearchInputSource;
  // Separates clears from narrowing searches because clearing pain usually means
  // waiting for the full panel bank to return, not for matched panels to narrow.
  isClearingSearch: boolean;
  // Latest edit flags, so the latest-input vital explains the specific edit that
  // produced this applied result.
  latestEditContext: PanelSearchInputEditContext;
  // Typed inputs that were replaced before they applied. This is the clearest
  // signal that the UI lagged behind manual typing corrections.
  supersededTypingInputCount: number;
  // Typing-source value changes in this burst. Paste counts as one typed input;
  // clear/history selections do not increment this counter.
  typingInputCount: number;
};

/**
 * Typing counter examples:
 * - type once, search applies:
 *   typingInputCount=1, supersededTypingInputCount=0
 * - type, then click a history item before the typed search applies:
 *   typingInputCount=1, supersededTypingInputCount=1
 * - click a history item directly:
 *   typingInputCount=0, supersededTypingInputCount=0
 * - clear directly:
 *   typingInputCount=0, supersededTypingInputCount=0
 */

type PanelSearchAppliedEvent = {
  // Avoid queuing duplicate afterFrame closes for the same target while we wait
  // for that committed query to reach the user-visible frame boundary.
  isApplyScheduled: boolean;
  // Local User Timing timestamp for the latest input. If the user abandons
  // search longer than the pairing window, the old first-input start cannot
  // close cleanly and later input should not inflate that stale burst's typing
  // counters.
  latestInputTime: number;
  operationKey: string;
  scopeStartTime: number;
  search: PanelSearchState;
  // Kept local for matching only; raw search text must not be sent to RUM.
  targetSearchQuery: string;
  // Invalidate queued afterFrame closes when a newer input supersedes the
  // target. Otherwise an old query's frame could close the vital for a newer
  // search before that newer query is actually applied.
  scheduledApplyVersion: number;
};

const EMPTY_EDIT_CONTEXT: PanelSearchInputEditContext = {
  hadDeletion: false,
  hadNonAppendEdit: false,
  hadPaste: false,
  hadReplacement: false,
};
const DEFAULT_SEARCH_CONTEXT: PanelSearchInputSearchContext = {
  inputSource: 'typing',
};

// Tracks the one in-flight panel-bank search operation so Datadog can see two
// user-pain shapes:
// - first input/change to applied search: total wait for a typing/edit burst
// - latest input/change to applied search: each repeated wait as the user churns
//   through consecutive applied queries
let panelSearchAppliedEvent: PanelSearchAppliedEvent | null = null;

export function useRecordPanelSearchInput({
  debounceMs,
  renderedSearchQuery,
}: {
  debounceMs: number;
  renderedSearchQuery: string;
}) {
  useEffect(() => clearPanelSearchAppliedEvent, []);

  return useCallback(
    (query: string, searchContext = DEFAULT_SEARCH_CONTEXT) => {
      recordPanelSearchInputChanged({
        debounceMs,
        query,
        renderedSearchQuery,
        searchContext,
      });
    },
    [debounceMs, renderedSearchQuery]
  );
}

export function useRecordPanelSearchResultsReady({
  renderedSearchQuery,
}: {
  renderedSearchQuery: string;
}) {
  useLayoutEffect(() => {
    clearIdlePanelSearchAppliedEvent(performance.now());
    // useLayoutEffect confirms the matching query committed, but it runs before
    // paint. Waiting for the next frame keeps this closer to user-visible
    // "search applied" instead of ending at internal React state readiness.
    const event = panelSearchAppliedEvent;
    if (
      event == null ||
      event.isApplyScheduled ||
      event.targetSearchQuery !== renderedSearchQuery
    ) {
      return;
    }

    event.isApplyScheduled = true;
    const scheduledApplyVersion = event.scheduledApplyVersion;
    afterFrame(timestamp => {
      if (
        panelSearchAppliedEvent !== event ||
        event.scheduledApplyVersion !== scheduledApplyVersion
      ) {
        return;
      }

      recordPanelSearchApplied({event, timestamp});
    });
  }, [renderedSearchQuery]);
}

function recordPanelSearchInputChanged({
  debounceMs,
  query,
  renderedSearchQuery,
  searchContext,
}: {
  debounceMs: number;
  query: string;
  renderedSearchQuery: string;
  searchContext: PanelSearchInputSearchContext;
}) {
  const nowMs = performance.now();
  clearIdlePanelSearchAppliedEvent(nowMs);
  const trimmedQuery = query.trim();
  const {inputSource} = searchContext;
  const isClearingSearch = trimmedQuery === '';
  const operationKey =
    panelSearchAppliedEvent?.operationKey ?? `panel_bank_search_${nowMs}`;

  recordPanelSearchInputAction({
    debounceMs,
    inputSource,
    isClearingSearch,
    operationKey,
    startTime: nowMs,
  });

  const edit = getSearchInputEditContext(searchContext);

  // If the applied query already matches the input, there is no additional
  // render/apply work to wait for. Still close an existing burst so undo-style
  // edits are visible instead of disappearing from RUM.
  if (trimmedQuery === renderedSearchQuery) {
    const event = panelSearchAppliedEvent;
    if (event == null) {
      return;
    }

    updatePanelSearchAppliedEvent({
      debounceMs,
      edit,
      event,
      inputSource,
      inputTime: nowMs,
      isClearingSearch,
      targetSearchQuery: trimmedQuery,
    });
    recordSearchAppliedStart({event, startTime: nowMs});
    recordPanelSearchApplied({event, timestamp: nowMs});
    return;
  }

  if (panelSearchAppliedEvent == null) {
    const event = createPanelSearchAppliedEvent({
      debounceMs,
      edit,
      inputSource,
      inputTime: nowMs,
      isClearingSearch,
      operationKey,
      targetSearchQuery: trimmedQuery,
    });

    panelSearchAppliedEvent = event;
    // The first changed input starts both pain views: total burst wait and this
    // specific input's wait to applied feedback.
    recordFirstInputStart({event, startTime: nowMs});
    recordSearchAppliedStart({event, startTime: nowMs});
    return;
  }

  updatePanelSearchAppliedEvent({
    debounceMs,
    edit,
    event: panelSearchAppliedEvent,
    inputSource,
    inputTime: nowMs,
    isClearingSearch,
    targetSearchQuery: trimmedQuery,
  });

  recordSearchAppliedStart({event: panelSearchAppliedEvent, startTime: nowMs});
}

function getSearchInputEditContext(
  searchContext: PanelSearchInputSearchContext
): PanelSearchInputEditContext {
  if (searchContext.inputSource !== 'typing') {
    return EMPTY_EDIT_CONTEXT;
  }
  return searchContext.editContext ?? EMPTY_EDIT_CONTEXT;
}

function recordPanelSearchInputAction({
  debounceMs,
  inputSource,
  isClearingSearch,
  operationKey,
  startTime,
}: {
  debounceMs: number;
  inputSource: SearchInputSource;
  isClearingSearch: boolean;
  operationKey: string;
  startTime: number;
}) {
  if (inputSource === 'typing') {
    return;
  }

  const mark =
    inputSource === 'clear'
      ? panelBankSearchClearMark
      : panelBankSearchHistoryItemMark;

  // Clear and history selection are deliberate non-typing interactions, so
  // count them as discrete RUM actions while keeping typing in duration context.
  mark.recordPerfMark({
    context: {
      search: {
        debounceMs,
        inputSource,
        isClearingSearch,
      },
    },
    devtools: {
      color: 'secondary-light',
      dataType: 'marker',
      properties: [
        ['operationKey', operationKey],
        ['search.inputSource', inputSource],
        ['search.isClearingSearch', String(isClearingSearch)],
      ],
      tooltipText:
        inputSource === 'clear'
          ? 'Panel search cleared'
          : 'Panel search history item selected',
    },
    operationKey,
    startTime,
  });
}

function createPanelSearchAppliedEvent({
  debounceMs,
  edit,
  inputSource,
  inputTime,
  isClearingSearch,
  operationKey,
  targetSearchQuery,
}: {
  debounceMs: number;
  edit: PanelSearchInputEditContext;
  inputSource: SearchInputSource;
  inputTime: number;
  isClearingSearch: boolean;
  operationKey: string;
  targetSearchQuery: string;
}): PanelSearchAppliedEvent {
  const scopeStartTime = getRumViewStartTimestamp();

  return {
    isApplyScheduled: false,
    latestInputTime: inputTime,
    operationKey,
    scopeStartTime,
    search: {
      appliedInputSource: inputSource,
      burstEditContext: edit,
      debounceMs,
      firstInputSource: inputSource,
      isClearingSearch,
      latestEditContext: edit,
      supersededTypingInputCount: 0,
      typingInputCount: inputSource === 'typing' ? 1 : 0,
    },
    targetSearchQuery,
    scheduledApplyVersion: 0,
  };
}

function updatePanelSearchAppliedEvent({
  debounceMs,
  edit,
  event,
  inputSource,
  inputTime,
  isClearingSearch,
  targetSearchQuery,
}: {
  debounceMs: number;
  edit: PanelSearchInputEditContext;
  event: PanelSearchAppliedEvent;
  inputSource: SearchInputSource;
  inputTime: number;
  isClearingSearch: boolean;
  targetSearchQuery: string;
}) {
  // A new input changes the pending target. Invalidate the old queued close,
  // then allow a new close to be scheduled when the new target query is actually
  // applied.
  event.scheduledApplyVersion += 1;
  event.isApplyScheduled = false;
  event.latestInputTime = inputTime;
  event.targetSearchQuery = targetSearchQuery;
  // Keep the first-input vital open to capture total burst wait, while
  // restarting the latest-input vital so each superseding edit has its own
  // wait-to-feedback measurement.
  const supersededInputSource = event.search.appliedInputSource;
  event.search = {
    ...event.search,
    appliedInputSource: inputSource,
    burstEditContext: mergeEditContexts(event.search.burstEditContext, edit),
    debounceMs,
    isClearingSearch,
    latestEditContext: edit,
    supersededTypingInputCount:
      event.search.supersededTypingInputCount +
      (supersededInputSource === 'typing' ? 1 : 0),
    typingInputCount:
      event.search.typingInputCount + (inputSource === 'typing' ? 1 : 0),
  };
}

function clearIdlePanelSearchAppliedEvent(nowMs: number) {
  const event = panelSearchAppliedEvent;
  if (event == null) {
    return;
  }

  if (
    nowMs - event.latestInputTime <=
    PANEL_BANK_SEARCH_MAX_PAIRING_DURATION_MS
  ) {
    return;
  }

  // Dropping the old open burst is better than emitting misleading pain context:
  // after this much idle time, Datadog will drop the original first-input vital
  // anyway, and the user's next input is a new search attempt rather than a
  // continuation of the abandoned burst.
  clearPanelSearchAppliedEvent();
}

function getFirstInputContext(
  event: PanelSearchAppliedEvent
): PanelSearchFirstInputContext {
  const {search} = event;
  return {
    search: {
      appliedInputSource: search.appliedInputSource,
      debounceMs: search.debounceMs,
      firstInputSource: search.firstInputSource,
      isClearingSearch: search.isClearingSearch,
      supersededTypingInputCount: search.supersededTypingInputCount,
      typingInputCount: search.typingInputCount,
      ...search.burstEditContext,
    },
  };
}

function getSearchAppliedContext(
  event: PanelSearchAppliedEvent
): PanelSearchAppliedContext {
  const {search} = event;
  return {
    search: {
      debounceMs: search.debounceMs,
      inputSource: search.appliedInputSource,
      isClearingSearch: search.isClearingSearch,
      supersededTypingInputCount: search.supersededTypingInputCount,
      typingInputCount: search.typingInputCount,
      ...search.latestEditContext,
    },
  };
}

function mergeEditContexts(
  current: PanelSearchInputEditContext,
  next: PanelSearchInputEditContext
): PanelSearchInputEditContext {
  return {
    hadDeletion: current.hadDeletion || next.hadDeletion,
    hadNonAppendEdit: current.hadNonAppendEdit || next.hadNonAppendEdit,
    hadPaste: current.hadPaste || next.hadPaste,
    hadReplacement: current.hadReplacement || next.hadReplacement,
  };
}

function recordFirstInputStart({
  event,
  startTime,
}: {
  event: PanelSearchAppliedEvent;
  startTime: number;
}) {
  panelBankSearchFirstInputToSearchAppliedDuration.recordStartPerfMark({
    context: getFirstInputContext(event),
    operationKey: event.operationKey,
    scopeStartTime: event.scopeStartTime,
    startTime,
  });
}

function recordSearchAppliedStart({
  event,
  startTime,
}: {
  event: PanelSearchAppliedEvent;
  startTime: number;
}) {
  panelBankSearchAppliedDuration.recordStartPerfMark({
    context: getSearchAppliedContext(event),
    operationKey: event.operationKey,
    scopeStartTime: event.scopeStartTime,
    startTime,
  });
}

function recordPanelSearchApplied({
  event,
  timestamp,
}: {
  event: PanelSearchAppliedEvent;
  timestamp: number;
}) {
  panelBankSearchFirstInputToSearchAppliedDuration.recordEndPerfMark({
    context: getFirstInputContext(event),
    operationKey: event.operationKey,
    scopeStartTime: event.scopeStartTime,
    startTime: timestamp,
  });

  panelBankSearchAppliedDuration.recordEndPerfMark({
    context: getSearchAppliedContext(event),
    operationKey: event.operationKey,
    scopeStartTime: event.scopeStartTime,
    startTime: timestamp,
  });

  panelSearchAppliedEvent = null;
}

function clearPanelSearchAppliedEvent() {
  panelSearchAppliedEvent = null;
}
